import { useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useQueryData } from '@hooks/useQuery'
import { CHART_COLORS } from 'src/constants/dashboard'
import {
  getUniqueColumnValues,
  isNumericDataset
} from 'src/utils/chartTransformers'
import type { ChartType, ValueOverride } from 'src/types/dashboard'

interface ChartOverridesEditorProps {
  query: string
  chartType: ChartType
  xAxis: string[]
  yAxis: string[]
  overrides: Record<string, ValueOverride>
  onChangeOverrides: (next: Record<string, ValueOverride>) => void
}

export default function ChartOverridesEditor({
  query,
  chartType,
  xAxis,
  yAxis,
  overrides,
  onChangeOverrides
}: ChartOverridesEditorProps) {
  const intl = useIntl()
  const { data } = useQueryData(query)

  const isNumericMap = useMemo(() => {
    if (chartType !== 'map-chart' || !data?.length || !yAxis[0]) return false
    return isNumericDataset(data.map((row) => row[yAxis[0]]))
  }, [data, chartType, yAxis])

  const keysToCustomize = useMemo(() => {
    if (!data?.length) return []

    if (chartType === 'pie-chart') {
      return getUniqueColumnValues(data, xAxis[0])
    }

    if (chartType === 'map-chart') {
      if (isNumericMap) return []
      return getUniqueColumnValues(data, yAxis[0])
    }

    return yAxis.filter((col) => col.trim() !== '')
  }, [data, chartType, xAxis, yAxis, isNumericMap])

  if (!isNumericMap && keysToCustomize.length === 0) {
    return null
  }

  const updateOverride = (
    key: string,
    field: 'label' | 'color',
    value: string | undefined
  ) => {
    const current = overrides[key] || {}
    const nextOverride = { ...current, [field]: value }

    if (!nextOverride.label && !nextOverride.color) {
      const nextOverrides = { ...overrides }
      delete nextOverrides[key]
      onChangeOverrides(nextOverrides)
    } else {
      onChangeOverrides({ ...overrides, [key]: nextOverride })
    }
  }

  const handleResetKey = (key: string) => {
    const nextOverrides = { ...overrides }
    delete nextOverrides[key]
    onChangeOverrides(nextOverrides)
  }

  const colorMinText = intl.formatMessage({
    id: 'item-editor.chart.overrides.color-min',
    defaultMessage: 'Color Mínimo'
  })

  const colorMaxText = intl.formatMessage({
    id: 'item-editor.chart.overrides.color-max',
    defaultMessage: 'Color Máximo'
  })

  return (
    <details className="chart-overrides-details">
      <summary>
        <h4>
          <FormattedMessage
            id="item-editor.chart.overrides.title"
            defaultMessage="Personalizar etiquetas y colores"
          />
        </h4>
      </summary>

      <div className="chart-overrides-list">
        {isNumericMap ? (
          <>
            <div className="chart-override-row">
              <label
                htmlFor="override-map-min"
                className="override-key"
                title={colorMinText}
              >
                <FormattedMessage id="item-editor.chart.overrides.color-min" />
              </label>
              <input
                id="override-map-min"
                type="color"
                value={overrides['__map_min__']?.color || CHART_COLORS[1]}
                onChange={(e) =>
                  updateOverride('__map_min__', 'color', e.target.value)
                }
              />
              {overrides['__map_min__']?.color && (
                <button
                  type="button"
                  className="reset-button"
                  onClick={() => handleResetKey('__map_min__')}
                  title="Restablecer"
                >
                  ×
                </button>
              )}
            </div>
            <div className="chart-override-row">
              <label
                htmlFor="override-map-max"
                className="override-key"
                title={colorMaxText}
              >
                <FormattedMessage id="item-editor.chart.overrides.color-max" />
              </label>
              <input
                id="override-map-max"
                type="color"
                value={overrides['__map_max__']?.color || CHART_COLORS[3]}
                onChange={(e) =>
                  updateOverride('__map_max__', 'color', e.target.value)
                }
              />
              {overrides['__map_max__']?.color && (
                <button
                  type="button"
                  className="reset-button"
                  onClick={() => handleResetKey('__map_max__')}
                  title="Restablecer"
                >
                  ×
                </button>
              )}
            </div>
          </>
        ) : (
          keysToCustomize.map((key, index) => {
            const override = overrides[key] || {}
            const hasOverride = Boolean(override.label || override.color)
            const defaultColor = CHART_COLORS[index % CHART_COLORS.length]
            const inputId = `override-input-${key}`

            return (
              <div key={key} className="chart-override-row">
                <label htmlFor={inputId} className="override-key" title={key}>
                  {key}
                </label>

                <input
                  id={inputId}
                  type="text"
                  placeholder={key}
                  value={override.label || ''}
                  onChange={(e) =>
                    updateOverride(
                      key,
                      'label',
                      e.target.value.trim() || undefined
                    )
                  }
                />

                <input
                  type="color"
                  value={override.color || defaultColor}
                  onChange={(e) =>
                    updateOverride(key, 'color', e.target.value || undefined)
                  }
                />

                {hasOverride && (
                  <button
                    type="button"
                    className="reset-button"
                    onClick={() => handleResetKey(key)}
                    title="Restablecer"
                  >
                    ×
                  </button>
                )}
              </div>
            )
          })
        )}
      </div>
    </details>
  )
}
